import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useDataTransfer } from '../../context/DataTransferContext';
import { useNotifications } from '../../context/NotificationContext';
import { useDatabaseSafe } from '../../context/DatabaseContext';
import { dsPackageApi } from '../../services/api';
import exportDsPackagePdf from '../../modules/development/dsPackagePdfExporter';
import { buildIndexSheetReportProps } from './indexSheetReportPdf';

/**
 * Export a package, or a subset of it, as one merged PDF.
 *
 * `recordIds` null means the whole package; a list means just those datasheets — reached from a
 * datasheet group or a single sheet's preview, so reprinting one instrument does not mean
 * regenerating a hundred pages.
 *
 * The watermark is NOT decided here. The server returns it from the package's real status, and
 * this renders what it is told. A non-SysAdmin who asks to suppress it is ignored server-side
 * and still gets a watermarked document.
 *
 * "Include index sheet reports" (on by default) puts each index sheet's "Index Report - Tags"
 * ahead of that index sheet's datasheets in the same PDF. The reports cover the index sheets the
 * exported datasheets sit on, narrowed to the package's tags on those sheets - the same rows the
 * Index Sheet Package section prints. If the reports cannot be run the export stops with an error
 * rather than producing a PDF that silently lacks them; unticking the box exports the datasheets
 * alone, exactly as without this option.
 */
// Mirrors DsPackageService.DraftWatermark - the server falls back to it when the box is blank.
const DRAFT_WATERMARK = 'Draft - Not for Construction';

export default function DsPackageExportModal({ pkg, recordIds = null, scopeLabel, onClose }) {
  const { isSysAdmin } = useAuth();
  const { notify } = useNotifications();
  const dbContext = useDatabaseSafe();
  const { registerActivity, updateActivity, completeActivity, failActivity } = useDataTransfer();
  const [suppress, setSuppress] = useState(false);
  const [busy, setBusy] = useState(false);
  const [includeReports, setIncludeReports] = useState(true);

  const approved = pkg.dsStatus === 'Approved';
  // The wording is the exporter's; whether a mark is REQUIRED is the server's. A package that is
  // not Approved always gets one - blank falls back to the draft wording there, not here.
  const [watermarkText, setWatermarkText] = useState(approved ? '' : DRAFT_WATERMARK);
  const suppressed = !approved && suppress && isSysAdmin;

  const run = async () => {
    setBusy(true);
    // A 120-sheet render takes real time. It goes in the activity tray rather than behind a
    // spinner on a button, so the user can get on with something else and still see it finish.
    const activityId = `dspackage-${pkg.dsPackage}-${Date.now()}`;
    registerActivity(activityId, {
      name: `ISA Datasheets ${pkg.dsPackage} Rev ${pkg.dsPackageRev}`,
      type: 'download',
      format: 'pdf',
      phase: 'Fetching snapshots…',
      pct: 0,
    });

    try {
      const res = await dsPackageApi.exportPackage(pkg.id, {
        recordIds: recordIds || undefined,
        suppressWatermark: suppress,
        watermarkText,
      });
      const { sheets, watermark, sheetCount, revisions } = res.data;

      if (!sheetCount) {
        failActivity(activityId);
        notify.error('Nothing to export — no snapshots in scope.');
        setBusy(false);
        return;
      }

      let printSheets = sheets;
      let indexSheetReports = null;
      if (includeReports) {
        updateActivity(activityId, { phase: 'Running index sheet reports…', pct: 15 });
        try {
          // The export's sheets carry the index sheet's label, not its ID; the package records
          // carry the ID, which is what the report rows are matched on.
          const recRes = await dsPackageApi.records(pkg.id);
          const recRows = recRes.data?.rows || [];
          const indexSheetByRecord = new Map(recRows.map(r => [r.id, r.indexSheetId ?? null]));
          printSheets = sheets.map(s => ({ ...s, indexSheetId: indexSheetByRecord.get(s.recordId) ?? null }));

          const indexSheetIds = [...new Set(printSheets.map(s => s.indexSheetId).filter(id => id != null))];
          const inScope = new Set(indexSheetIds);
          const tagRowIds = [...new Set(recRows
            .filter(r => r.indexSheetId != null && inScope.has(r.indexSheetId) && r.tagRowId != null)
            .map(r => r.tagRowId))];

          indexSheetReports = indexSheetIds.length === 0 ? {} : await buildIndexSheetReportProps({
            indexSheetIds,
            tagRowIds,
            sourceLabel: pkg.sourceLabel,
            currentDb: dbContext?.currentDb,
            onProgress: (phase) => { if (phase) updateActivity(activityId, { phase }); },
          });
        } catch (err) {
          const why = err?.response?.data?.error || err?.message || 'unknown error';
          throw new Error(`Index sheet reports failed (${why}). No PDF was written - untick "Include index sheet reports" to export the datasheets alone.`);
        }
      }

      updateActivity(activityId, { phase: `Rendering ${sheetCount} datasheet${sheetCount === 1 ? '' : 's'}…`, pct: 40 });

      await exportDsPackagePdf({
        sheets: printSheets,
        watermark,
        indexSheetReports,
        metadata: {
          dsPackage: pkg.dsPackage,
          dsPackageRev: pkg.dsPackageRev,
          partial: !!recordIds,
          // Fills each sheet's revision block; see DsSheetPage.
          revisions: revisions || [],
        },
        onProgress: (phase) => { if (phase) updateActivity(activityId, { phase }); },
      });

      completeActivity(activityId);
      onClose?.();
    } catch (err) {
      failActivity(activityId);
      notify.error(err?.response?.data?.error || err?.message || 'Export failed.');
      setBusy(false);
    }
  };

  return (
    <div className="stui-modal-overlay" onClick={busy ? undefined : onClose}>
      <div className="stui-modal stui-modal-md" onClick={e => e.stopPropagation()}>
        <div className="stui-modal-header">
          <span className="stui-modal-title">Export {pkg.dsPackage} Rev {pkg.dsPackageRev}</span>
          <button className="stui-fk-select-btn" onClick={onClose} disabled={busy} title="Close"><X size={20} /></button>
        </div>

        <div className="stui-modal-body">
          <div className="stui-detail-grid">
            <div className="stui-field-group stui-col-12">
              <label>Scope</label>
              <div className="stui-field-readonly">
                {recordIds ? (scopeLabel || `${recordIds.length} selected datasheet(s)`) : 'Whole package'}
              </div>
            </div>
            <div className="stui-field-group stui-col-12">
              <label>Package Status</label>
              <div className="stui-field-readonly">{pkg.dsStatus}</div>
            </div>
            <div className="stui-field-group stui-col-24">
              <label>Watermark</label>
              {suppressed ? (
                <div className="stui-field-readonly">None — suppressed</div>
              ) : (
                <input
                  className="stui-field-input"
                  value={watermarkText}
                  onChange={e => setWatermarkText(e.target.value)}
                  maxLength={60}
                  disabled={busy}
                  placeholder={approved ? 'None — type to mark this copy' : DRAFT_WATERMARK}
                />
              )}
              <p className="stui-hint">
                {approved
                  ? 'Printed on every page. Leave empty for a clean copy.'
                  : 'Printed on every page. Left empty, the draft wording is used.'}
              </p>
            </div>
          </div>

          {!approved && isSysAdmin && (
            <>
              {/* SysAdmin only, and off by default: a clean pre-approval print should be a
                  deliberate act, not a side effect of who happens to be logged in. */}
              <label className="stui-checkbox">
                <input
                  type="checkbox"
                  className="stui-checkbox-input"
                  checked={suppress}
                  onChange={e => setSuppress(e.target.checked)}
                  disabled={busy}
                />
                {' '}Suppress the draft watermark
              </label>
              {suppress && (
                <p className="stui-hint">
                  This package is <strong>{pkg.dsStatus}</strong>. An unwatermarked copy is
                  indistinguishable from an approved, issued document.
                </p>
              )}
            </>
          )}

          <label className="stui-checkbox">
            <input
              type="checkbox"
              className="stui-checkbox-input"
              checked={includeReports}
              onChange={e => setIncludeReports(e.target.checked)}
              disabled={busy}
            />
            {' '}Include index sheet reports
          </label>
          <p className="stui-hint">
            Each index sheet's report prints ahead of its datasheets. Reports are not watermarked.
          </p>

          {!approved && !isSysAdmin && (
            <p className="stui-hint">
              Exports carry the draft watermark until the package is Approved.
            </p>
          )}
        </div>

        <div className="stui-modal-footer">
          <button className="stui-btn stui-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="stui-btn stui-btn-primary" onClick={run} disabled={busy}>
            {busy ? 'Exporting…' : 'Export PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}
